import { useCallback, useMemo, useState } from 'react';

import { type ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  debounce,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useLocale } from '../../../../src/hooks/locale';
import { REALADVISOR_TENANT_ID } from '../../../../src/utils/tenant';
import { gql } from '../../../__generated__';
import {
  type LeadAgentFragment,
  Lead_Agent_Source_Enum_Enum,
  type Lead_Agents_Bool_Exp,
  type Lead_Agents_Set_Input,
} from '../../../__generated__/graphql';
import { CreateListingModal } from '../../../components/create-listing/CreateListingModal';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '../../../components/form/RaForm';
import { IndicatorLeadStage } from '../../../components/IndicatorLeadStage';
import { MutationErrorSnackbar } from '../../../components/MutationErrorModal';
import { useAppData } from '../../../providers/AppDataProvider';
import {
  GET_LEAD_AGENTS,
  GET_PIPELINES,
  UPDATE_LEAD_AGENTS,
} from '../leadsQueries';

interface LeadQualificationProps {
  leadId: string;
}

// Define a type for our form values
interface QualificationFormValues {
  agent_id?: string | null;
  win_probability?: number | null;
  predicted_win_date?: string | null;
  stage_id?: string | null;
  tags?: any[] | null; // Use any[] to avoid type conflicts
}

const GET_LEAD_DATA = gql(/* GraphQL */ `
  query GetLeadData($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      id
      lots {
        id
      }
      leads_tags {
        id
        created_by
        created_at
        dictionary {
          id
          label
        }
      }
    }
    dictionaries(where: { type: { _eq: tags_types } }) {
      id
      label
    }
    lead_stages(
      where: {
        pipeline: { name: { _eq: "brokerage" } }
        name: { _eq: "is_a_duplicate" }
      }
      limit: 1
    ) {
      id
    }
  }
`);

const UPDATE_LEAD_TAGS = gql(/* GraphQL */ `
  mutation UpdateLeadTags(
    $removedTags: [uuid!]!
    $newTags: [leads_tags_insert_input!]!
  ) {
    delete_leads_tags(where: { id: { _in: $removedTags } }) {
      affected_rows
    }
    insert_leads_tags(objects: $newTags) {
      affected_rows
    }
  }
`);

const SELF_ASSIGN_LEAD_AGENT = gql(/* GraphQL */ `
  mutation SelfAssignLeadAgent(
    $lead_id: uuid!
    $broker_id: uuid!
    $stage_id: uuid!
  ) {
    update_leads_by_pk(
      pk_columns: { id: $lead_id }
      _set: { stage_id: $stage_id }
    ) {
      id
      stage_id
      stage {
        id
        label
      }
    }
    insert_lead_agents_one(
      object: {
        lead_id: $lead_id
        user_id: $broker_id
        source: crm
        stage_id: $stage_id
      }
    ) {
      id
      ...LeadAgent
    }
  }
`);

export const LeadQualification = ({ leadId }: LeadQualificationProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [targetStage, setTargetStage] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<ApolloError | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const myId = me?.id;
  const isAdmin = me?.is_admin;
  const hasBrokerageModule = me?.modules?.includes('brokerage');
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_LEAD_AGENTS, {
    variables: { lead_id: leadId },
  });

  const { data: pipelinesData, loading: pipelinesLoading } =
    useQuery(GET_PIPELINES);

  const { data: leadData } = useQuery(GET_LEAD_DATA, {
    variables: { lead_id: leadId },
  });

  const duplicateStage = leadData?.lead_stages[0]?.id;

  const [updateLeadAgent] = useMutation(UPDATE_LEAD_AGENTS, {
    onError: setUpdateError,
  });
  const [updateLeadTags] = useMutation(UPDATE_LEAD_TAGS, {
    refetchQueries: [GET_LEAD_DATA],
    onError: setUpdateError,
  });
  const [selfAssignLeadAgent] = useMutation(SELF_ASSIGN_LEAD_AGENT, {
    refetchQueries: [GET_LEAD_AGENTS],
    onError: setUpdateError,
  });

  // Extract pipeline stages from query result
  const pipelines = useMemo(
    () => pipelinesData?.pipelines || [],
    [pipelinesData],
  );

  const debouncedUpdateLeadAgent = useMemo(
    () => debounce(updateLeadAgent, 3000),
    [updateLeadAgent],
  );

  const uniqueAgents = useMemo(() => {
    if (data == null) {
      return [];
    }

    // Create a map to dedupe lead_agents by unique user IDs
    const map = new Map<string, LeadAgentFragment>(
      data.lead_agents
        .toSorted((a, b) => {
          // Prioritize non-crm sources so they appear first in the array and CRM source override the items of the map
          if (a.source !== 'crm' && b.source === 'crm') {
            return -1;
          }
          if (a.source === 'crm' && b.source !== 'crm') {
            return 1;
          }
          return 0;
        })
        .map(agent => [agent.user.id, agent]),
    );

    return Array.from(map.values());
  }, [data]);

  const leadAgentsOptions = useMemo(
    () =>
      uniqueAgents.map(agent => ({
        value: agent.user.id,
        label: `${agent.user.first_name} ${agent.user.last_name}`,
      })),
    [uniqueAgents],
  );

  // Get the current lead agent
  const selectedLeadAgent = isAdmin
    ? // Admins can select any agent (first one is automatically selected if only one agent is assigned)
      uniqueAgents.length === 1
      ? uniqueAgents[0]
      : uniqueAgents.find(agent => agent.user.id === selectedAgentId)
    : // Agents see their own or the first one
      uniqueAgents.find(agent => agent.user.id === myId) || uniqueAgents[0];

  const defaultValues = useMemo(
    () => ({
      agent_id: selectedLeadAgent?.user.id,
      win_probability: selectedLeadAgent?.win_probability || null,
      predicted_win_date: selectedLeadAgent?.predicted_win_date || null,
      stage_id: selectedLeadAgent?.stage_id || null,
      tags:
        leadData?.leads_by_pk?.leads_tags
          ?.map(tag => tag.dictionary)
          ?.filter(item => item != null) || [],
    }),
    [selectedLeadAgent, leadData?.leads_by_pk?.leads_tags],
  );

  const updateLeadAgentHelper = useCallback(
    (
      where: Lead_Agents_Bool_Exp,
      set: Lead_Agents_Set_Input,
      leadStage?: string,
    ) => {
      // Temporary solution that should be removed when result_page agents are active.
      // We only want to update the lead stage for non-admin (because they cannot see lead_agents with source "result_page") and for admin only if the selected lead agent is not a result page agent.
      const updateLead =
        leadStage != null &&
        (!me?.is_admin ||
          selectedLeadAgent?.source !== Lead_Agent_Source_Enum_Enum.ResultPage);

      updateLeadAgent({
        variables: {
          where,
          set,
          lead_id: leadId,
          lead_stage_id: leadStage,
          update_lead: updateLead,
        },
      });
    },
    [updateLeadAgent, leadId, me, selectedLeadAgent],
  );

  // Form definition for lead agent qualification data
  const formDefinition: FormDefinitionType<QualificationFormValues> = useMemo(
    () => () =>
      [
        {
          type: 'tags',
          name: 'tags',
          label: t('Tags'),
          gridProps: { md: 12 },
          onFilter: async (inputStr: string) => {
            return (leadData?.dictionaries ?? []).filter(
              option =>
                option.label
                  ?.toLowerCase?.()
                  ?.includes?.(inputStr.toLowerCase()) ?? false,
            );
          },
          renderOption(props, option) {
            return <li {...props}>{option.label}</li>;
          },
          getOptionLabel: option => option.label,
          renderTags: (value, getTagProps) => {
            return value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={option.label} {...tagProps} />;
            });
          },
          render: () => isAdmin ?? false,
        },
        {
          type: 'select',
          name: 'agent_id',
          label: t('Select agent to qualify'),
          gridProps: { md: 12 },
          render: () => (isAdmin && leadAgentsOptions.length > 1) ?? false,
          options: () => leadAgentsOptions,
        },
        {
          type: 'select',
          name: 'stage_id',
          label: t('Pipeline step'),
          gridProps: { md: 12 },
          options: () =>
            pipelines
              .map(pipeline => [
                {
                  value: pipeline.id,
                  label: pipeline.label || '',
                  isHeader: true,
                },
                ...pipeline.stages.map(stage => ({
                  value: stage.id,
                  label: stage.label || '',
                })),
              ])
              .flat(),
          renderOption: option => {
            if (option.isHeader) {
              return (
                <ListSubheader key={option.value}>{option.label}</ListSubheader>
              );
            }
            const status = pipelines
              .flatMap(pipeline => pipeline.stages)
              .find(stage => stage.id === option.value)?.status;
            return (
              <MenuItem
                key={option.value}
                value={option.value ?? ''}
                disabled={option.disabled ?? false}
              >
                <IndicatorLeadStage status={status}>
                  {option.label}
                </IndicatorLeadStage>
              </MenuItem>
            );
          },
          render: ({ agent_id }) => !!agent_id,
        },
        {
          type: 'select',
          name: 'win_probability',
          label: t('Win Probability'),
          gridProps: { md: 12 },
          // Generate options from 5 to 100 in steps of 5
          options: () =>
            Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map(value => ({
              value: value.toString(),
              label: `${value}%`,
            })),
          render: ({ agent_id }) => !!agent_id,
        },
        {
          type: 'date',
          name: 'predicted_win_date',
          label: t('Predicted Win Date'),
          gridProps: { md: 12 },
          render: ({ agent_id }) => !!agent_id,
        },
      ],
    [isAdmin, leadAgentsOptions, leadData?.dictionaries, pipelines, t],
  );

  const handleFormChange = useCallback<RaFormOnChange<QualificationFormValues>>(
    async (formData, field, type) => {
      if (type !== 'change') {
        return;
      }

      if (field === 'agent_id' && formData.agent_id != null) {
        setSelectedAgentId(formData.agent_id);
        return;
      }

      if (field === 'tags') {
        // Diff the tags from the lead and the new tags
        const newTags = formData.tags?.filter(
          tag =>
            !leadData?.leads_by_pk?.leads_tags.some(
              t => t.dictionary?.id === tag.id,
            ),
        );
        const removedTags = leadData?.leads_by_pk?.leads_tags.filter(
          tag => !formData.tags?.some(t => t.id === tag.dictionary?.id),
        );
        if (newTags || removedTags) {
          return await updateLeadTags({
            variables: {
              newTags:
                newTags?.map(tag => ({
                  lead_id: leadId,
                  dictionary_id: tag.id,
                })) || [],
              removedTags: removedTags?.map(tag => tag.id) || [],
            },
          });
        }
        return;
      }

      if (formData.agent_id == null || field == null) {
        return;
      }

      const whereUpdateClause: Lead_Agents_Bool_Exp = me?.is_admin
        ? {
            lead_id: { _eq: leadId },
            user: {
              teams_users: {
                team: { teams_users: { user_id: { _eq: formData.agent_id } } },
              },
            },
          }
        : {
            lead_id: { _eq: leadId },
            user: {
              teams_users: {
                team_id: { _in: me?.teams.map(team => team.team_id) },
              },
            },
          };

      if (field === 'predicted_win_date') {
        // use debounce, do not update the date until the user has stopped typing
        return debouncedUpdateLeadAgent({
          variables: {
            where: whereUpdateClause,
            set: { predicted_win_date: formData.predicted_win_date },
            lead_id: leadId,
            update_lead: false,
          },
        });
      }

      if (field === 'stage_id') {
        const selectedStage = pipelines
          .flatMap(pipeline => pipeline.stages)
          .find(stage => stage.id === formData.stage_id);

        if (selectedStage?.status === 'won') {
          setTargetStage(selectedStage.id);

          if ((leadData?.leads_by_pk?.lots?.length ?? 0) === 0) {
            // If we don't have a listing, we need to create it before updating the agent's stage.
            return;
          }
        }
      }

      updateLeadAgentHelper(
        whereUpdateClause,
        {
          [field]: formData[field as keyof QualificationFormValues],
        },
        field === 'stage_id' ? formData.stage_id ?? undefined : undefined,
      );
    },
    [
      me,
      debouncedUpdateLeadAgent,
      leadId,
      leadData?.leads_by_pk,
      pipelines,
      updateLeadAgentHelper,
      updateLeadTags,
      setSelectedAgentId,
    ],
  );

  if (loading || pipelinesLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Alert severity="error">{error.message}</Alert>
      </Paper>
    );
  }

  return (
    <>
      <RaForm
        formDefinition={formDefinition}
        defaultValues={defaultValues}
        onChange={handleFormChange}
        gridProps={{
          p: 0,
        }}
        // Hide action buttons by providing an empty component
        actionButtonsComponent={<></>}
      />
      {data != null && data.lead_agents.length === 0 && (
        <Stack
          direction="column"
          spacing={2}
          sx={{ mt: 2 }}
          alignItems="center"
        >
          <Alert severity="warning">
            {t(
              'No agents found for this lead. Please assign an agent to start qualifying.',
            )}
          </Alert>
          {me?.is_admin &&
            me?.tenant_id === REALADVISOR_TENANT_ID &&
            duplicateStage != null && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() =>
                  selfAssignLeadAgent({
                    variables: {
                      lead_id: leadId,
                      broker_id: me.id,
                      stage_id: duplicateStage,
                    },
                  })
                }
              >
                {t('Mark as duplicate')}
              </Button>
            )}
        </Stack>
      )}
      {(leadData?.leads_by_pk?.lots?.length ?? 0) === 0 && (
        <CreateListingModal
          fromLeadId={leadId}
          brokerId={selectedLeadAgent?.user.id}
          open={targetStage != null}
          onClose={() => setTargetStage(null)}
          onListingCreated={lotId => {
            if (selectedLeadAgent != null && targetStage != null) {
              // We update all lead_agents rows in the same teams as the selected agent.
              updateLeadAgentHelper(
                me?.is_admin
                  ? {
                      lead_id: { _eq: leadId },
                      user: {
                        teams_users: {
                          team: {
                            teams_users: {
                              user_id: { _eq: selectedLeadAgent.user.id },
                            },
                          },
                        },
                      },
                    }
                  : {
                      lead_id: { _eq: leadId },
                      user: {
                        teams_users: {
                          team_id: {
                            _in: me?.teams.map(team => team.team_id),
                          },
                        },
                      },
                    },
                { stage_id: targetStage },
                targetStage,
              );
            }

            if (hasBrokerageModule) {
              navigate(`/listings/${lotId}`);
            }
          }}
        />
      )}
      {updateError != null && (
        <MutationErrorSnackbar
          error={updateError}
          onClose={() => setUpdateError(null)}
        />
      )}
    </>
  );
};
